import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  Target, 
  Zap, 
  Search, 
  Brain, 
  Activity,
  FileText,
  ArrowUp,
  Download,
  Settings
} from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSpace } from '../context/SpaceContext';
import { useTier } from '../contexts/TierContext';
import { buildBillingUpgradeUrl } from '../utils/billingNavigation';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { InlineLoading } from '../components/LoadingStates';
import { SearchInsights } from '../components/SearchInsights';
import { ProductivityDashboard } from '../components/ProductivityDashboard';
import apiService from '../services/api';

interface MemoryAnalytics {
  totalMemories: number;
  memoriesCreated30Days: number;
  enhancementRate: number;
  searchSuccessRate: number;
  qualityScore: number;
  timeSpent: number;
  totalSearches: number;
  averageSearchTime: number;
  topTags: Array<{ tag: string; count: number }>;
  spaceBreakdown: Array<{
    spaceId: string;
    spaceName: string;
    memoryCount: number;
    qualityScore: number;
    usage: 'high' | 'medium' | 'low';
    enhancementRate: number;
    searchSuccessRate: number;
    timeSaved: number;
    topTags: string[];
  }>;
  weeklyTrends: Array<{
    date: string;
    memoriesCreated: number;
    searchQueries: number;
    enhanced: number;
  }>;
  integrationHealth?: {
    overallScore: number;
  };
  integrationRequestMetrics?: {
    successRate: number;
    totalEvents: number;
    avgProcessingTime: number;
    activeAlerts: number;
  };
}

interface OptimizationRecommendation {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impact: string;
  estimatedTime: string;
  confidence: number;
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { currentSpace } = useSpace();
  const { canAccess } = useTier();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<MemoryAnalytics | null>(null);
  const [recommendations, setRecommendations] = useState<OptimizationRecommendation[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedSpaceDetail, setSelectedSpaceDetail] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const spaceIdForApi = currentSpace?.space_id ?? undefined;

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Loading analytics data...', { timeRange, spaceId: spaceIdForApi });

      const analyticsResponse = await apiService.getMemoryAnalyticsOverview(
        timeRange,
        spaceIdForApi
      );

      console.log('✅ Analytics overview loaded successfully:', {
        analyticsResponse: !!analyticsResponse,
      });

      setAnalyticsData(analyticsResponse);

      apiService
        .getOptimizationRecommendations()
        .then((recommendationsResponse) => {
          setRecommendations(recommendationsResponse);
        })
        .catch((error) => {
          console.warn('⚠️ Recommendations failed to load (non-blocking):', error);
          setRecommendations([]);
        });
      setError(null);

      analytics.trackEvent('analytics_loaded', {
        timeRange,
        spaceId: spaceIdForApi,
        totalMemories: analyticsResponse?.totalMemories || 0,
      });
    } catch (error: unknown) {
      console.error('❌ Failed to load analytics data:', error);

      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred while loading analytics data');
      }

      analytics.trackEvent('analytics_load_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timeRange,
        spaceId: spaceIdForApi,
      });

      setAnalyticsData(null);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [timeRange, spaceIdForApi]);

  useEffect(() => {
    analytics.trackEvent('page_view', {
      page: 'analytics',
      timeRange,
      spaceId: spaceIdForApi,
    });
    loadAnalyticsData();
  }, [timeRange, spaceIdForApi, loadAnalyticsData]);

  const filteredSpaceData = useMemo(
    () => analyticsData?.spaceBreakdown ?? [],
    [analyticsData]
  );

  const getQualityBadge = (score: number) => {
    if (score >= 9) return { text: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (score >= 8) return { text: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (score >= 7) return { text: 'Fair', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Needs Work', color: 'bg-red-100 text-red-800' };
  };

  const getUsageBadge = (usage: string) => {
    const badges = {
      high: { text: 'High', color: 'bg-green-100 text-green-800' },
      medium: { text: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
      low: { text: 'Low', color: 'bg-gray-100 text-gray-800' }
    };
    return badges[usage as keyof typeof badges] || badges.low;
  };

  const getPriorityBadge = (priority: string) => {
    const badges = {
      high: { text: 'High Impact', color: 'bg-red-100 text-red-800' },
      medium: { text: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
      low: { text: 'Low Priority', color: 'bg-gray-100 text-gray-800' }
    };
    return badges[priority as keyof typeof badges] || badges.low;
  };

  const handleSpaceDetailView = (space: any) => {
    setSelectedSpaceDetail(space);
    setDetailSheetOpen(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <InlineLoading size="lg" message="Loading Memory Intelligence..." />
        </div>
      </div>
    );
  }

  if (!analyticsData && !loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto text-center py-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Analytics Unavailable</h2>
          <p className="text-muted-foreground mb-4">Unable to load analytics data. Please try again.</p>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <p className="text-red-800 text-sm">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}
          <Button onClick={loadAnalyticsData} disabled={loading}>
            {loading ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
      </div>
    );
  }

  // TypeScript assertion: analyticsData is guaranteed to be non-null after early return check
  if (!analyticsData) {
    return null;
  }

  return (
    <>
    <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('analytics.title', { ns: 'dashboard' })}
          </h1>
          <p className="text-muted-foreground">
            {t('analytics.subtitle', { ns: 'dashboard' })}
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="dashboard-glass-toolbar flex flex-col sm:flex-row gap-4 items-center justify-between p-4"
        >
          <div className="flex gap-4 items-center">
            <Select value={timeRange} onValueChange={(value) => setTimeRange(value as '7d' | '30d' | '90d')}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export Report
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Export analytics data as CSV or PDF report</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure analytics preferences and data retention</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </motion.div>

        {/* Key Metrics Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="dashboard-metric-tile cursor-help">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <FileText className="w-5 h-5 text-indigo-600" />
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      +{analyticsData.memoriesCreated30Days}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-foreground">{analyticsData.totalMemories}</div>
                    <div className="text-sm text-muted-foreground">Total Memories</div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Total number of memories stored across all your spaces</p>
                <p className="text-xs text-muted-foreground mt-1">+{analyticsData.memoriesCreated30Days} new memories this month</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="dashboard-metric-tile cursor-help">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Zap className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      +{((analyticsData.enhancementRate || 0) * 0.1).toFixed(1)}%
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-foreground">{analyticsData.enhancementRate}%</div>
                    <div className="text-sm text-muted-foreground">Enhancement Rate</div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Percentage of memories that have been AI-enhanced with tags and structure</p>
                <p className="text-xs text-muted-foreground mt-1">Higher rates improve searchability and organization</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="dashboard-metric-tile cursor-help">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Search className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      +{((analyticsData.searchSuccessRate || 0) * 0.05).toFixed(1)}%
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-foreground">{analyticsData.searchSuccessRate}%</div>
                    <div className="text-sm text-muted-foreground">Search Success</div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('analytics.searchSuccessTooltip', { ns: 'dashboard' })}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('analytics.searchSuccessTooltipSecondary', { ns: 'dashboard' })}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="dashboard-metric-tile cursor-help">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      +{((analyticsData.timeSpent || 0) * 0.2).toFixed(1)}h
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-2xl font-bold text-foreground">{analyticsData.timeSpent}h</div>
                    <div className="text-sm text-muted-foreground">Time Saved This Month</div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Estimated time saved by quickly finding information through memory search</p>
                <p className="text-xs text-muted-foreground mt-1">Based on average research time vs. instant memory retrieval</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </motion.div>

        {/* Real Analytics Integration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}
          >
            <ProductivityDashboard showDetailedView={true} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <SearchInsights showDetailedView={true} />
          </motion.div>
        </div>

        {/* Memory Intelligence Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="dashboard-glass-panel"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              📊 Memory Processing Overview (Last 30 Days)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600">{analyticsData.totalSearches}</div>
                <div className="text-sm text-muted-foreground">Total Searches</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{analyticsData.averageSearchTime}s</div>
                <div className="text-sm text-muted-foreground">Avg Search Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{analyticsData.qualityScore}/10</div>
                <div className="text-sm text-muted-foreground">Overall Quality</div>
              </div>
            </div>
          </div>

          {/* Memory Intelligence Insights */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Memory Type Breakdown */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Brain className="w-5 h-5 text-purple-600" />
                    </div>
                                         <div>
                       <h3 className="text-lg font-semibold text-foreground">{t('memoryIntelligence.memoryTypes', { ns: 'dashboard' })}</h3>
                       <p className="text-sm text-muted-foreground">{t('memoryIntelligence.memoryTypeBreakdown', { ns: 'dashboard' })}</p>
                     </div>
                   </div>
                   {!canAccess('advanced_analytics') && (
                     <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                       {t('memoryIntelligence.premium', { ns: 'dashboard' })}
                     </Badge>
                   )}
                 </div>
                 
                 <div className="space-y-3">
                   <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                     <div className="flex items-center space-x-2">
                       <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                       <span className="text-sm font-medium">{t('memoryIntelligence.textMemories', { ns: 'dashboard' })}</span>
                     </div>
                     <span className="text-sm font-bold text-foreground">{analyticsData.totalMemories}</span>
                   </div>
                   
                   {canAccess('advanced_analytics') ? (
                     <>
                       <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg opacity-60">
                         <div className="flex items-center space-x-2">
                           <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                           <span className="text-sm font-medium">{t('memoryIntelligence.imageMemories', { ns: 'dashboard' })}</span>
                         </div>
                         <span className="text-sm text-muted-foreground">{t('memoryIntelligence.comingSoon', { ns: 'dashboard' })}</span>
                       </div>
                       
                       <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg opacity-60">
                         <div className="flex items-center space-x-2">
                           <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                           <span className="text-sm font-medium">{t('memoryIntelligence.audioMemories', { ns: 'dashboard' })}</span>
                         </div>
                         <span className="text-sm text-muted-foreground">{t('memoryIntelligence.comingSoon', { ns: 'dashboard' })}</span>
                       </div>
                     </>
                   ) : (
                     <div className="text-center py-4">
                       <p className="text-sm text-muted-foreground mb-3">{t('memoryIntelligence.upgradeForMultimodal', { ns: 'dashboard' })}</p>
                       <Button 
                         size="sm" 
                         onClick={() =>
                           navigate(
                             buildBillingUpgradeUrl({
                               returnPath: '/dashboard?view=analytics',
                               source: 'analytics_multimodal',
                             })
                           )
                         }
                         className="bg-purple-600 hover:bg-purple-700"
                       >
                         {t('memoryIntelligence.upgradeToPro', { ns: 'dashboard' })}
                       </Button>
                     </div>
                   )}
                 </div>
               </div>

               {/* Memory Growth Insights */}
               <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                 <div className="flex items-center justify-between mb-4">
                   <div className="flex items-center space-x-3">
                     <div className="p-2 bg-green-100 rounded-lg">
                       <TrendingUp className="w-5 h-5 text-green-600" />
                     </div>
                     <div>
                       <h3 className="text-lg font-semibold text-foreground">{t('memoryIntelligence.growthInsights', { ns: 'dashboard' })}</h3>
                       <p className="text-sm text-muted-foreground">{t('memoryIntelligence.memoryCollectionTrends', { ns: 'dashboard' })}</p>
                     </div>
                   </div>
                 </div>
                 
                 <div className="space-y-4">
                   <div>
                     <div className="flex items-center justify-between mb-2">
                       <span className="text-sm font-medium text-foreground">{t('memoryIntelligence.thisMonth', { ns: 'dashboard' })}</span>
                       <span className="text-sm text-muted-foreground">{analyticsData.memoriesCreated30Days} {t('memoryIntelligence.newMemories', { ns: 'dashboard' })}</span>
                     </div>
                     <div className="w-full bg-muted rounded-full h-2">
                       <div 
                         className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                         style={{ width: `${Math.min((analyticsData.memoriesCreated30Days / 100) * 100, 100)}%` }}
                       ></div>
                     </div>
                   </div>
                   
                   <div className="text-sm text-muted-foreground">
                     {analyticsData.memoriesCreated30Days > 50 
                       ? t('memoryIntelligence.growingRapidly', { ns: 'dashboard' })
                       : analyticsData.memoriesCreated30Days > 20
                       ? t('memoryIntelligence.growingSteadily', { ns: 'dashboard' })
                       : t('memoryIntelligence.startBuilding', { ns: 'dashboard' })
                     }
                   </div>
                 </div>
               </div>
            </div>
          </div>

          {/* Integration Health & Webhook Monitoring */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Integration Health & Monitoring</h3>
            {analyticsData.integrationHealth && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border bg-card/90 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Overall Health Score</span>
                    <span className={`text-sm font-bold ${
                      analyticsData.integrationHealth.overallScore >= 80 ? 'text-green-600' : 
                      analyticsData.integrationHealth.overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {analyticsData.integrationHealth.overallScore}/100
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        analyticsData.integrationHealth.overallScore >= 80 ? 'bg-green-500' : 
                        analyticsData.integrationHealth.overallScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${analyticsData.integrationHealth.overallScore}%` }}
                    ></div>
                  </div>
                </div>
                
                {analyticsData.integrationRequestMetrics && (
                  <>
                    <div className="rounded-lg border border-border bg-card/90 p-4">
                      <div className="text-sm font-medium text-muted-foreground mb-1">Integration success rate</div>
                      <div className="text-2xl font-bold text-indigo-600">{analyticsData.integrationRequestMetrics.successRate.toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">{analyticsData.integrationRequestMetrics.totalEvents} requests (24h)</div>
                    </div>
                    
                    <div className="rounded-lg border border-border bg-card/90 p-4">
                      <div className="text-sm font-medium text-muted-foreground mb-1">Avg processing time</div>
                      <div className="text-2xl font-bold text-purple-600">{analyticsData.integrationRequestMetrics.avgProcessingTime}ms</div>
                      <div className="text-xs text-muted-foreground">
                        {analyticsData.integrationRequestMetrics.activeAlerts > 0 ? 
                          `${analyticsData.integrationRequestMetrics.activeAlerts} active alerts` : 
                          'No active alerts'
                        }
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Space-by-Space Intelligence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="dashboard-glass-panel"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              🏠 Space-by-Space Intelligence
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            {filteredSpaceData.map((space) => {
              const qualityBadge = getQualityBadge(space.qualityScore);
              const usageBadge = getUsageBadge(space.usage);
              
              return (
                <div key={space.spaceId} className="border border-border rounded-lg p-6 bg-muted/15">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Brain className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-foreground">
                          {space.spaceName} ({space.memoryCount} memories)
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${qualityBadge.color}`}>
                            Quality: {space.qualityScore}/10
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${usageBadge.color}`}>
                            Usage: {usageBadge.text}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSpaceDetailView(space)}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Detailed View
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Enhancement Rate</div>
                      <div className="font-medium">{space.enhancementRate}% ({Math.floor(space.memoryCount * space.enhancementRate / 100)} memories enhanced)</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Search Success</div>
                      <div className="font-medium">{space.searchSuccessRate}%</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Top Tags</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {space.topTags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-muted text-foreground rounded text-xs">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Productivity Impact</div>
                      <div className="font-medium">
                        {space.timeSaved > 0 ? `${space.timeSaved} hours saved this month` : 'Privacy protected'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* AI-Powered Optimization Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="dashboard-glass-panel"
        >
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              💡 AI-Powered Optimization Recommendations
            </h2>
            <p className="text-muted-foreground">
              Intelligent suggestions to improve your memory system performance
            </p>
          </div>
          
          <div className="p-6 space-y-4">
            {recommendations.map((rec) => {
              const priorityBadge = getPriorityBadge(rec.priority);
              
              return (
                <div key={rec.id} className="border border-border rounded-lg p-4 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityBadge.color}`}>
                          {priorityBadge.text}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {rec.confidence}% confidence
                        </span>
                      </div>
                      
                      <h3 className="font-medium text-foreground mb-1">{rec.title}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
                      
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>Impact: {rec.impact}</span>
                        <span>Time: {rec.estimatedTime}</span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 ml-4">
                      <Button size="sm" variant="outline">
                        Learn More
                      </Button>
                      <Button size="sm">
                        Apply Now
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-wrap gap-4 justify-center"
        >
          <Button variant="outline">
            <TrendingUp className="w-4 h-4 mr-2" />
            Deep Dive Analytics
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Full Report
          </Button>
          <Button variant="outline">
            <Settings className="w-4 h-4 mr-2" />
            Optimize Settings
          </Button>
          <Button variant="outline">
            <Activity className="w-4 h-4 mr-2" />
            Compare Periods
          </Button>
        </motion.div>
      </div>

      {/* Space Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[600px] sm:w-[800px] overflow-y-auto">
          {selectedSpaceDetail && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-indigo-600" />
                  {selectedSpaceDetail.spaceName} - Detailed Analytics
                </SheetTitle>
                <SheetDescription>
                  Comprehensive analytics and insights for this memory space
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Space Overview */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-indigo-600">{selectedSpaceDetail.memoryCount}</div>
                    <div className="text-sm text-muted-foreground">Total Memories</div>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{selectedSpaceDetail.qualityScore}/10</div>
                    <div className="text-sm text-muted-foreground">Quality Score</div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="rounded-lg border border-border bg-card p-4">
                  <h3 className="font-medium text-foreground mb-4">Performance Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Enhancement Rate</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-muted rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full transition-all"
                            style={{ width: `${selectedSpaceDetail.enhancementRate}%` }}
                          ></div>
                        </div>
                        <span className="font-medium">{selectedSpaceDetail.enhancementRate}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Search Success Rate</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-muted rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${selectedSpaceDetail.searchSuccessRate}%` }}
                          ></div>
                        </div>
                        <span className="font-medium">{selectedSpaceDetail.searchSuccessRate}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Tags */}
                <div className="rounded-lg border border-border bg-card p-4">
                  <h3 className="font-medium text-foreground mb-4">Popular Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedSpaceDetail.topTags.map((tag: string, index: number) => (
                      <span 
                        key={index} 
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Productivity Impact */}
                <div className="rounded-lg border border-border bg-card p-4">
                  <h3 className="font-medium text-foreground mb-4">Productivity Impact</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-yellow-600" />
                        <div>
                          <div className="font-medium text-foreground">Time Saved</div>
                          <div className="text-sm text-muted-foreground">This month</div>
                        </div>
                      </div>
                      <div className="text-xl font-bold text-yellow-600">
                        {selectedSpaceDetail.timeSaved > 0 ? `${selectedSpaceDetail.timeSaved}h` : 'Private'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Pattern */}
                <div className="rounded-lg border border-border bg-card p-4">
                  <h3 className="font-medium text-foreground mb-4">Usage Pattern</h3>
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getUsageBadge(selectedSpaceDetail.usage).color}`}>
                      {getUsageBadge(selectedSpaceDetail.usage).text} Usage
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t('analytics.usagePatternFootnote', { ns: 'dashboard' })}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-border">
                  <Button className="flex-1">
                    <Target className="w-4 h-4 mr-2" />
                    Optimize Space
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
} 